import { Component, ElementRef, OnDestroy, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { NgSelectComponent } from "@ng-select/ng-select";
import {
  AlertController,
  Events,
  LoadingController,
  NavController,
  NavParams,
  ToastController,
  ViewController,
} from "ionic-angular";
import SortOptions from "models/content-wrapper/sort-options";
import { DeclinedAction } from "models/order-management/declinedAction";
import {
  Action,
  DropdownItem,
  Order,
  OrderComponent,
  OrderErrorResponse,
  OrderTypeConfiguration,
} from "models/order-management/models";
import { OrderResponse } from "models/order-management/orderResponse";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import CompanyResourceType from "models/resource-management/company-resource-type.enum";
import CredentialedResource from "models/resource-management/credentialed-resource";
import * as moment from "moment-timezone";
import { DocManagementDataService } from "providers/doc-management-data-service/doc-management-data-service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  filter,
  mergeMap,
  startWith,
  switchMap,
  take,
} from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import Utils from "utils/Utilities";
import { DiscontinuedAction } from "../../../models/order-management/discontinuedAction";
import { OrderPatchRequest } from "../../../models/order-management/orderPatchRequest";

@Component({
  selector: "discontinue-order",
  templateUrl: "discontinue-order.html",
})
export default class DiscontinueOrder implements OnDestroy {
  @ViewChild("orderingProviderId") orderingProviderInput: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };
  @ViewChild("inputFile") inputFile: ElementRef;

  approvalType: string;
  approvalTypeConfiguration;
  credentialedResource: CredentialedResource;
  dataTransfer = new DataTransfer();
  futureDiscontinueDate: boolean = false;
  discontinueForm: FormGroup;
  idFiles: string[] = [];
  isProfessionalProvider: boolean;
  isSameProvider = false;
  isServiceDeclined: boolean = false;
  listFiles = [];
  numberOfFiles = 0;
  order: OrderResponse;
  orderComponent: OrderComponent;
  orderTypeConfigurations: Array<OrderTypeConfiguration> = [];
  patientChartId: number = 0;
  providerDropdownItems$: Observable<DropdownItem[]>;
  providerQuery$ = new Subject<string | CredentialedResource>();
  readBackAndVerified: boolean;
  selectedProvider: DropdownItem;
  shouldIncludeReadBackAndVerified: boolean = false;
  showToaster = false;
  spinner: any = null;
  spinnerCounter: number = 0;
  userDisplayName: string;
  userProviderDisplayName: string;
  get isPending() {
    return (
      this.order.discontinuedAction?.approvalType ===
      Order.ApprovalTypeEnum.Pending
    );
  }
  get title() {
    return this.isPending
      ? "Proceed with Discontinuation"
      : "Discontinue Order";
  }

  isDischargedPatient: boolean;
  dischargedDatetime: string;

  public constructor(
    private fb: FormBuilder,
    public navCtrl: NavController,
    private navParams: NavParams,
    private alertController: AlertController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private resourceManagementService: ResourceManagementService,
    private orderManagementDataService: OrderManagementDataService,
    public events: Events,
    private navBarService: NavBarServiceProvider,
    private dataService: DocManagementDataService,
    public viewCtrl: ViewController,
    public utilityService: UtilityService
  ) {
    this.order = this.navParams.data.order;
    this.userDisplayName = this.navParams.data.userDisplayName;
    this.credentialedResource = this.navParams.data.credentialedResource;
    this.orderTypeConfigurations = this.navParams.data.orderTypeConfigurations;
    this.isDischargedPatient = this.navParams.data.isDischargedPatient;
    this.dischargedDatetime = this.navParams.data.dischargedDatetime;
    this.isProfessionalProvider = isProfessionalProvider(
      this.credentialedResource
    );
    this.userProviderDisplayName = this.credentialedResource.findDisplayName(
      new Date()
    );
    const discontinueDate = this.getDiscontinueDate();
    const todaysDate = moment().startOf("day");
    discontinueDate.setHours(0, 0, 0, 0);
    this.hideReadBackAndVerified();

    this.discontinueForm = new FormGroup({
      discontinueDate: this.fb.control(
        this.getDefaultDate(discontinueDate),
        Validators.required
      ),
      discontinueProvider: this.fb.control(
        null,
        this.getRequiredUnlessServiceDeclinedValidator()
      ),
      discontinueReason: this.fb.control("", null),
      approvalType: this.fb.control(null, this.approvalTypeValidator),
      readBackAndVerified: this.fb.control(
        false,
        this.readBackAndVerifiedValidator
      ),
      isServiceDeclined: this.fb.control(false),
      declinedReason: this.fb.control(""),
      declinedDate: this.fb.control(
        todaysDate,
        this.getRequiredIfServiceDeclinedValidator()
      ),
    });

    this.defaultDiscontinueDateBasedOnOrderType();

    if (discontinueDate >= todaysDate && !this.isPending) {
      this.futureDiscontinueDate = true;
    }
  }

  /****************************************************************************
   * Component Lifecyle Methods                                               *
   ****************************************************************************/
  ngOnInit() {
    this.providerSearch();
    this.approvalTypeConfiguration = this.orderTypeConfigurations
      .find((o) => o.orderType === this.order.orderType)
      .components?.find(
        (c) => c.componentName === "radio-group" && !c.componentLabel
      );
  }

  ngOnDestroy(): void {}

  /****************************************************************************
   * Getters                                                                  *
   ****************************************************************************/
  get discontinueDate(): string {
    const selectedDate = this.discontinueForm.get("discontinueDate").value;
    if (selectedDate) {
      let discontinueDate: string;
      const currentDate = moment();
      const selectedDateTime = moment(selectedDate);

      if (selectedDateTime.isSame(currentDate, "day")) {
        discontinueDate = currentDate.toISOString();
      } else {
        discontinueDate = selectedDateTime.endOf("day").toISOString();
      }
      return discontinueDate;
    }
    return null; // Return null if no date is selected
  }

  get discontinueReason(): string {
    return this.discontinueForm.get("discontinueReason").value;
  }

  get declinedReason(): string {
    return this.discontinueForm.get("declinedReason").value;
  }

  get declinedDate(): string {
    const selectedDate = this.discontinueForm.get("declinedDate").value;
    if (selectedDate) {
      const currentDate = moment().startOf("day");
      const selectedDateTime = moment(selectedDate);

      if (
        selectedDateTime.isSame(currentDate) ||
        selectedDateTime.isBefore(currentDate)
      ) {
        return selectedDateTime.startOf("day").toISOString();
      } else {
        return selectedDateTime.endOf("day").toISOString();
      }
    }
    return null; // Return null if no date is selected
  }

  getValidatorBasedOnServiceDeclined(requiredWhenServiceDeclinedIs = true) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (requiredWhenServiceDeclinedIs === this.isServiceDeclined) {
        return Validators.required(control);
      }

      return null;
    };
  }

  getRequiredIfServiceDeclinedValidator() {
    return this.getValidatorBasedOnServiceDeclined();
  }

  getRequiredUnlessServiceDeclinedValidator() {
    return this.getValidatorBasedOnServiceDeclined(false);
  }

  /****************************************************************************
   * Helper Functions                                                         *
   ****************************************************************************/
  defaultDiscontinueDateBasedOnOrderType() {
    // If the order is a "Visit Frequency" type, we don't want to set a default
    // date due to "User Error" where Users don't double-check the defaulted
    // "Discontinue Date" field prior to submission (CL-1101)
    if (this.order.orderType === "VISIT_FREQUENCY") {
      this.discontinueForm.get("discontinueDate").setValue(null);
      return;
    }
  }

  hideReadBackAndVerified() {
    const orderTypeConfiguration = this.orderTypeConfigurations.find(
      (o) => o.orderType === this.order.orderType
    );
    const readBackAndVerified = orderTypeConfiguration.components.some((c) =>
      c.properties.some((p) => p.name === "readBackAndVerified")
    );
    this.shouldIncludeReadBackAndVerified = readBackAndVerified;
  }

  selectApprovalType(event) {
    if (event == Order.ApprovalTypeEnum.Verbal) {
      this.listFiles = [];
    } else {
      this.readBackAndVerified = false;
      this.discontinueForm.get("readBackAndVerified").setValue(false);
    }
    this.approvalType = event;
    this.recheckValidity();
  }

  readBackVerifiedChange(event) {
    this.readBackAndVerified = event.target.checked;
  }

  serviceDeclinedChange(event) {
    this.isServiceDeclined = event.target.checked;
    this.discontinueForm
      .get("discontinueDate")
      .setValue(moment().startOf("day"));
    this.recheckValidity();
  }

  async uploadFile() {
    const internalListFiles = this.listFiles.filter((x) => x !== undefined);
    if (internalListFiles.length != 0) {
      await Promise.all(
        internalListFiles.map(async (file) => {
          return new Promise((resolve, reject) => {
            let payload = {
              key: this.navBarService.getExternalId(),
              label: Utils.getFileName(file.name),
              serviceName: "PatientChart",
              fileExtension: file.name.slice(file.name.lastIndexOf(".")),
              description: "Order File",
              category: "Provider Orders",
              patientID: this.navBarService.getExternalId(),
            };
            let body = new FormData();
            body.append("file", file);
            body.append("tags", JSON.stringify(payload));

            this.dataService.uploadDocument(body).subscribe(
              (data) => {
                this.idFiles.push(data["documentId"].toString());
                resolve(null);
              },
              (err) => {
                console.error(err.error);
                this.viewCtrl.dismiss({});
                reject(err);
              }
            );
          });
        })
      );
    }
    this.save();
  }

  selectFile(e) {
    const { files } = e.target;
    for (let i = 0; i < files.length; i++) {
      if (files[i].size < 10485760) {
        this.listFiles.push(files[i]);
        this.dataTransfer.items.add(e.target.files[i]);
      } else {
        this.showToaster = true;
      }
    }
    if (this.showToaster) {
      this.utilityService.presentCustomErrorToast(
        "Some files exceed the size limit."
      );
      this.showToaster = false;
    }
    e.target.files = this.dataTransfer.files;
  }

  deleteFile(event: any, index: number) {
    this.dataTransfer.items.remove(index);
    const newFileList = [];
    this.inputFile.nativeElement.files = this.dataTransfer.files;
    for (let i = 0; i < this.listFiles.length; i++) {
      newFileList.push(this.dataTransfer.files[i]);
    }
    this.listFiles = newFileList;
  }

  saveFiles() {
    if (this.approvalType == Order.ApprovalTypeEnum.Written) {
      this.uploadFile();
    } else {
      this.save();
    }
  }

  validateForm() {
    let approvalTypeOK = false;
    if (this.isProfessionalProvider) {
      approvalTypeOK = true;
    } else if (this.isServiceDeclined) {
      approvalTypeOK = true;
    } else if (
      [
        Order.ApprovalTypeEnum.Written,
        Order.ApprovalTypeEnum.NoSignatureRequired,
      ].includes(this.approvalType as Order.ApprovalTypeEnum)
    ) {
      approvalTypeOK = true;
    } else if (this.approvalType == Order.ApprovalTypeEnum.Verbal) {
      if (
        this.order.orderType == "TREATMENT" ||
        this.order.orderType == "OTHER"
      ) {
        approvalTypeOK = this.readBackAndVerified;
      } else {
        approvalTypeOK = true;
      }
    }
    return this.discontinueForm.valid && approvalTypeOK;
  }

  /**
   * Sets the the providerDropdownItems observable only if the user is not a provider.
   * Otherwise, the provider is the ordering provider and the field is disabled.
   * @returns
   */
  providerSearch() {
    this.providerDropdownItems$ = this.providerQuery$.pipe(
      startWith(this.isProfessionalProvider ? this.credentialedResource : null),
      switchMap<string | CredentialedResource | null, DropdownItem[]>(
        (query) => {
          if (typeof query === "string") {
            return of(query).pipe(
              filter((query) => !query || ![1, 2].includes(query.length)), // do nothing if 1 or 2 characters
              switchMap((query: string | null) => {
                // null -> clear, '' -> field cleared by backspace
                if (!query || query.length === 0) {
                  return of([]);
                }
                return this.resourceManagementService
                  .getCompanyResourceSummaries(
                    0,
                    100,
                    query ? query : undefined,
                    new SortOptions("last_name", "asc"),
                    CompanyResourceType.PROVIDER
                  )
                  .pipe(
                    mergeMap((providerList) =>
                      of(
                        providerList.content.map((provider) => {
                          return {
                            label: provider.displayName,
                            value: `${provider.externalId}`,
                          };
                        })
                      )
                    ),
                    catchError(() => of([]))
                  );
              })
            );
          } else if (query) {
            return of([
              {
                label: this.userProviderDisplayName,
                value: `${query.externalId}`,
              },
            ]);
          }
          return of([]);
        }
      )
    );
    if (this.isProfessionalProvider) {
      this.providerDropdownItems$.pipe(take(1)).subscribe((_) => {
        this.onProviderSelected({
          label: this.userProviderDisplayName,
          value: `${this.credentialedResource.externalId}`,
        });
      });
    }
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick() {
    this.hiddenInput.setFocus();
    this.orderingProviderInput.filterInput.nativeElement.focus();
  }

  onProviderSelected(event: DropdownItem) {
    if (event) {
      this.selectedProvider = event;
      this.userProviderDisplayName = this.selectedProvider.label;
      this.discontinueForm
        .get("discontinueProvider")
        .setValue(this.selectedProvider.value);
      this.isSameProvider =
        event.label === this.credentialedResource.findDisplayName();
      this.recheckValidity();
    }
  }

  approvalTypeValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (!this.isSameProvider && !this.isServiceDeclined) {
      return Validators.required(control);
    }

    return null;
  };

  readBackAndVerifiedValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (
      !this.isSameProvider &&
      !this.isServiceDeclined &&
      this.shouldIncludeReadBackAndVerified &&
      this.approvalType == Order.ApprovalTypeEnum.Verbal
    ) {
      return Validators.required(control);
    }

    return null;
  };

  recheckValidity() {
    for (const form of [
      "discontinueProvider",
      "discontinueReason",
      "approvalType",
      "readBackAndVerified",
      "declinedDate",
    ])
      this.discontinueForm.get(form).updateValueAndValidity();
  }

  getDefaultDate(discontinueDate: Date): string | null {
    if (this.isPending) {
      return null;
    } else if (
      (this.order.discontinueDate ||
        (this.isDischargedPatient && this.dischargedDatetime)) &&
      !isNaN(discontinueDate.getTime())
    ) {
      return discontinueDate.toString();
    }
    return new Date().toDateString();
  }

  /****************************************************************************
   * Spinner Functions                                                        *
   ****************************************************************************/
  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  /****************************************************************************
   * Save & Cancel Functions                                                  *
   ****************************************************************************/
  save(): void {
    // If the user is the provider, use their information,
    // otherwise use the provider selected from the dropdown.
    const provider =
      this.isProfessionalProvider && this.isSameProvider
        ? {
            orderingProviderId: this.credentialedResource.externalId,
            orderingProviderDisplayName: this.userProviderDisplayName,
            approvalType: Order.ApprovalTypeEnum.Written,
            eSign: true,
          }
        : {
            orderingProviderId: this.selectedProvider
              ? parseInt(this.selectedProvider.value)
              : null,
            orderingProviderDisplayName: this.selectedProvider
              ? this.selectedProvider.label
              : null,
          };

    let discontinuePatch: OrderPatchRequest;
    if (this.isServiceDeclined) {
      let declinedAction: DeclinedAction = {
        ...provider,
        eSign: undefined,
        actionType: Action.ActionTypeEnum.Declined,
        displayName: this.userDisplayName,
        reason: this.discontinueReason,
        declinedReason: this.declinedReason,
        declined: true,
        timezone: moment.tz.guess(),
        approvalType: "noSignatureRequired",
        readBackAndVerified: false,
      };
      discontinuePatch = {
        discontinueDate: new Date(this.discontinueDate).toISOString(),
        declinedDate: new Date(this.declinedDate).toISOString(),
        ...declinedAction,
      };
    } else {
      let discontinuedAction: DiscontinuedAction = {
        actionType: Action.ActionTypeEnum.Discontinued,
        reason: this.discontinueReason,
        displayName: this.userDisplayName,
        timezone: moment.tz.guess(),
        approvalType: this.approvalType as Order.ApprovalTypeEnum,
        readBackAndVerified: this.approvalType == Order.ApprovalTypeEnum.Verbal,
        documentIds: this.idFiles,
        ...provider,
      };
      discontinuePatch = {
        discontinueDate: new Date(this.discontinueDate).toISOString(),
        ...discontinuedAction,
      };
    }

    this.increaseSpinnerCount("Saving ...");

    this.orderManagementDataService
      .updateOrder(this.order.orderId, discontinuePatch)
      .pipe(take(1))
      .finally(() => this.decreaseSpinnerCount())
      .subscribe((x) => {
        this.navCtrl.pop();
      }, OrderErrorResponse.handleError(this.toastCtrl, "Error discontinuing order."));
  }

  cancel(): void {
    /**
     * Handles the cancel event
     */
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present().then((r) => console.log("Handled"));
  }

  getDiscontinueDate() {
    if (this.isDischargedPatient) {
      return new Date(this.dischargedDatetime);
    }
    return new Date(this.order.discontinueDate);
  }

  get showLOCWarningMessage() {
    return this.isDischargedPatient && this.order.orderType === "LEVEL_OF_CARE";
  }
}



// WEBPACK FOOTER //
// ./src/pages/orders-entry-om/discontinue-order/discontinue-order.ts